/**
 * DOCUMENTATION:
 * Component: UiUxWireframing
 * Purpose: Educational page detailing the prototyping phase and translating Figma concepts to code.
 * * * Note for Junior Developers:
 * - You do not need to be a master designer, but you MUST know how to navigate Figma.
 * - Handoff is a two-way street. If a designer creates a component that is technically impossible
 * or highly unperformant to build in CSS, you need the vocabulary to push back constructively.
 * - Figma's "Auto Layout" is literally just CSS Flexbox under the hood!
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function UiUxWireframing() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Wireframing Concepts
    const wireframingConcepts = useMemo(() => [
        { concept: "Lo-Fi Wireframes", role: "The Blueprint", description: "Low-Fidelity. Grey boxes and placeholder text. Focuses purely on structure, user flow, and spacing. No colors or fonts allowed." },
        { concept: "Hi-Fi Prototypes", role: "The Final Coat", description: "High-Fidelity. Pixel-perfect representations of the final app, including brand colors, typography, images, and clickable interactions." },
        { concept: "Design Tokens", role: "The Variables", description: "The core values of a brand (colors, font sizes, shadow values) defined in Figma, which developers translate directly into CSS Variables." },
        { concept: "Auto Layout", role: "The Flexbox", description: "A Figma feature that allows designers to create dynamic frames that grow or shrink. It translates perfectly to CSS Flexbox." },
    ], []);

    // Coding examples demonstrating how Figma translates to Code
    const codingModules = useMemo(() => [
        {
            title: "1. Translating Design Tokens to CSS",
            code: `/* Inside Figma, the designer sets 'Brand Primary' to #DAA520. \n   As a developer, you MUST NOT hardcode that hex value everywhere. \n   Translate it to a token immediately! */\n\n:root {\n  /* Colors */\n  --token-color-primary: #DAA520;\n  --token-color-surface: #1a0f35;\n  \n  /* Spacing (4px grid system) */\n  --token-space-sm: 8px;\n  --token-space-md: 16px;\n  --token-space-lg: 32px;\n}\n\n.card {\n  background-color: var(--token-color-surface);\n  padding: var(--token-space-lg);\n}`,
            desc: "When the design team eventually decides to change the primary brand color from gold to blue, you only have to change it in one place (the `:root`) instead of searching through 50 different CSS files."
        },
        {
            title: "2. Figma Auto Layout to CSS Flexbox",
            code: `/* Figma Specs panel says:\n   - Auto Layout: Horizontal\n   - Spacing between items: 16px\n   - Padding: 24px \n   - Alignment: Center */\n\n.figma-frame {\n  /* Translation to CSS */\n  display: flex;\n  flex-direction: row;     /* Horizontal */\n  gap: 16px;               /* Spacing */\n  padding: 24px;           /* Padding */\n  align-items: center;     /* Alignment */\n  justify-content: center; /* Alignment */\n}`,
            desc: "Modern designers use Auto Layout in Figma instead of dragging and dropping elements freely. Once you realize that Auto Layout is literally just a UI wrapper for Flexbox, translating designs to code becomes incredibly easy."
        },
        {
            title: "3. Figma Components to React Components",
            code: `/* Figma provides a 'Button' component with 'Variants' (Primary, Outline, Text) */\n\n// React Translation\nexport default function Button({ variant = 'primary', children, onClick }) {\n  \n  // Map the Figma variant to a CSS BEM Modifier class\n  const modifierClass = \n    variant === 'outline' ? 'btn--outline' : \n    variant === 'text' ? 'btn--text' : \n    'btn--primary';\n\n  return (\n    <button \n      className={\`btn \${modifierClass}\`} \n      onClick={onClick}\n    >\n      {children}\n    </button>\n  );\n}`,
            desc: "When a designer builds a Master Component in Figma with variants, your job is to build a React Component that takes those exact same variants as 'props'. This ensures the design system and code repository stay in perfect sync."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    WIREFRAMING & FIGMA
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Speaking the Designer's Language
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            A developer's life is vastly improved when they understand how to read a Figma file. The modern design handoff is no longer a static JPG; it is a rich, inspectable file containing CSS properties, SVG assets, and interactive prototyping flows. By understanding how designers structure their files—using Design Tokens, Components, and Auto Layout—you can write cleaner, more modular frontend code.
                        </Typography>

                        {/* ✅ Core Philosophies Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>The Design Pipeline</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Concept</div>
                                    <div>Role in Pipeline</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {wireframingConcepts.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.concept}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.role}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/uiux-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
